import {
  ActionIcon,
  Anchor,
  Badge,
  Center,
  Divider,
  Group,
  Menu,
  ScrollArea,
  Text,
  Tooltip
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconBellFilled,
  IconBellRinging,
  IconBolt,
  IconDatabaseExclamation,
  IconProps,
  IconUpload
} from '@tabler/icons-react'
import { Fragment, ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { useDeviceDetect } from '@/hooks/useDeviceDetect'
import { showToast } from '@/theme/notifications'
import { Notification, NotificationType } from '@/types/notifications'
import {
  useGetNotifications,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead
} from '../../queries/notificationQueries'
import { InfiniteScroll } from '../ui-shared/InfiniteScroll/InfiniteScroll'
import { NotificationItem } from './NotificationItem'
import { getNotificationLink } from './getNotificationLink'

const NOTIFICATION_MESSAGES: Record<
  NotificationType,
  { message: string; icon: ReactElement<IconProps> }
> = {
  [NotificationType.MODEL_TRAINING]: {
    message: 'notifications.message.modelTraining',
    icon: <IconBolt />
  },
  [NotificationType.FILE_ACTION]: {
    message: 'notifications.message.fileUpload',
    icon: <IconUpload />
  },
  [NotificationType.MODEL_TEST_DATA_PER_MONTH_EXCEEDED]: {
    message: 'notifications.message.modelTestDataPerMonthExceeded',
    icon: <IconAlertTriangle />
  },
  [NotificationType.MODEL_TRAININGS_PER_MONTH_EXCEEDED]: {
    message: 'notifications.message.modelTrainingsPerMonthExceeded',
    icon: <IconAlertTriangle />
  },
  [NotificationType.EVENT_STORAGE_LIMIT_EXCEEDED]: {
    message: 'notifications.message.eventStorageLimitExceeded',
    icon: <IconDatabaseExclamation />
  },
  [NotificationType.DATASET_STORAGE_LIMIT_EXCEEDED]: {
    message: 'notifications.message.datasetStorageLimitExceeded',
    icon: <IconDatabaseExclamation />
  }
}

export const NotificationDropdown = () => {
  const navigate = useNavigate()
  const intl = useIntl()
  const { isMobile } = useDeviceDetect()

  const [opened, setOpened] = useState(false)

  const { mutateAsync: markOneAsReadAsync } = useMarkNotificationAsRead()
  const { mutateAsync: markAllAsReadAsync } = useMarkAllNotificationsAsRead()

  const { data, hasNextPage, isFetching, refetch, fetchNextPage } =
    useGetNotifications()

  const notifications = data?.pages.flatMap((page) => page.results) ?? []

  const unreadCount = notifications.filter(
    (notification) => !notification.read_at
  ).length

  const handleMenuOpen = () => {
    setOpened(true)
    void refetch()
  }

  const markOneAsRead = async (id: string) => {
    try {
      await markOneAsReadAsync(id)
      void refetch()
    } catch {
      showToast(
        intl.formatMessage({ id: 'notifications.errorMarkRead' }),
        'error'
      )
    }
  }

  const markAllAsRead = async () => {
    try {
      await markAllAsReadAsync()
      void refetch()
    } catch {
      showToast(
        intl.formatMessage({ id: 'notifications.errorMarkRead' }),
        'error'
      )
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    const notificationLink = getNotificationLink(
      notification.notification_type,
      notification.notification_context
    )

    // Navigate immediately to the notification link
    void navigate(notificationLink)

    // If the notification is unread, mark it as read asynchronously in the background
    if (!notification.read_at) {
      void markOneAsRead(notification.id)
    }
  }

  return (
    <Menu
      opened={opened}
      width={isMobile ? 360 : 640}
      shadow="md"
      withArrow
      onOpen={handleMenuOpen}
      onClose={() => setOpened(false)}
    >
      <Menu.Target>
        <Center pos="relative">
          <Tooltip
            label={<FormattedMessage id="header.toolbar.notifications" />}
          >
            <ActionIcon variant="subtle" color="gray.0" radius="xl" size="sm">
              <IconBellRinging style={{ width: '80%', height: '80%' }} />
            </ActionIcon>
          </Tooltip>

          {unreadCount > 0 && (
            <Badge pos="absolute" top={-8} right={-10} color="red" size="xs">
              {unreadCount}
            </Badge>
          )}
        </Center>
      </Menu.Target>

      <Menu.Dropdown>
        <Group justify="space-between" p="sm">
          <Group wrap="nowrap" gap="xs">
            <IconBellFilled size={16} color="var(--mantine-color-yellow-7)" />

            <Text fw={600} size="sm">
              <FormattedMessage id="notifications" />
            </Text>
          </Group>

          {unreadCount > 0 && (
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => void markAllAsRead()}
            >
              <FormattedMessage id="notifications.markAllRead" />
            </Anchor>
          )}
        </Group>

        <ScrollArea
          h={400}
          viewportProps={{
            style: {
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 'var(--mantine-radius-xs)',
              paddingBottom: '10px' // padding is required for infinite scroll to trigger load more correctly
            }
          }}
        >
          {notifications.length === 0 && !isFetching && (
            <Center mih={200}>
              <Text size="sm">
                <FormattedMessage id="notifications.noNotifications" />
              </Text>
            </Center>
          )}

          <InfiniteScroll
            hasMore={hasNextPage}
            isFetching={isFetching}
            loaderSize={24}
            onLoadMore={() => void fetchNextPage()}
          >
            {notifications.map((notification, index) => (
              <Fragment key={notification.id}>
                <NotificationItem
                  message={
                    <FormattedMessage
                      id={
                        NOTIFICATION_MESSAGES[notification.notification_type]
                          .message
                      }
                    />
                  }
                  icon={
                    NOTIFICATION_MESSAGES[notification.notification_type].icon
                  }
                  dateTime={notification.created_at}
                  isRead={!!notification.read_at}
                  onClick={() => handleNotificationClick(notification)}
                  onMarkAsRead={() => void markOneAsRead(notification.id)}
                />

                {index !== notifications.length - 1 && <Divider />}
              </Fragment>
            ))}
          </InfiniteScroll>
        </ScrollArea>
      </Menu.Dropdown>
    </Menu>
  )
}
